<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['toggle']);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const count = computed(() =>
  dynamicCount.value > 99 ? '99+' : dynamicCount.value
);
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    class="flex items-center gap-3 px-3.5 py-2 rounded-xl h-9 min-w-0 transition-all duration-200 relative"
    role="button"
    draggable="false"
    :to="to"
    :title="label"
    :class="{
      'text-primary bg-primary/10 font-semibold active':
        isActive && !hasActiveChild,
      'text-foreground font-semibold': hasActiveChild,
      'text-muted-foreground hover:bg-accent/30 hover:text-foreground':
        !isActive && !hasActiveChild,
    }"
    @click.stop="emit('toggle')"
  >
    <div
      v-if="isActive && !hasActiveChild"
      class="absolute ltr:left-1.5 rtl:right-1.5 top-1/2 -translate-y-1/2 w-[3px] h-3.5 bg-primary rounded-full"
    />
    <div v-if="icon" class="relative flex items-center justify-center">
      <Icon v-if="icon" :icon="icon" class="size-4.5" />
      <span
        v-if="showBadge"
        class="size-2 -top-0.5 -right-0.5 bg-primary absolute rounded-full border-2 border-background shadow-sm"
      />
    </div>
    <div
      class="flex items-center gap-1.5 flex-grow justify-between min-w-0 flex-1"
    >
      <span
        class="truncate text-sm tracking-tight"
        :class="{
          'font-medium': !isActive && !hasActiveChild,
          'font-semibold': isActive || hasActiveChild,
        }"
      >
        {{ label }}
      </span>
      <span
        v-if="dynamicCount && !expandable"
        class="ml-auto rounded-full text-[10px] leading-none h-4 min-w-[1rem] flex items-center justify-center font-bold px-1 flex-shrink-0 bg-primary/10 text-primary border border-primary/20"
      >
        {{ count }}
      </span>
    </div>
    <span
      v-if="expandable"
      class="size-3 text-muted-foreground transition-transform duration-200"
      :class="[
        isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down',
        { 'rotate-180': isExpanded },
      ]"
      @click.stop="emit('toggle')"
    />
  </component>
</template>
